import { Injectable, inject, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import {
  AutoQuoteStorageStateKey,
  CashFlowsBlotterGridStateKey,
  DciGridDefaultsStateKey,
  DefaultsStateKey,
  EventsBlotterGridStateKey,
  ExcludedTradeStatusesKey,
  PNLBlotterGridStateKey,
  PNLBlotterTimeZoneStateKey,
  ReportingCurrencyStateKey,
  RfqSettingsStateKey,
  RiskBlotterGridStateKey,
  RiskBlotterTimeZoneStateKey,
  StrategyDetailTabsStateKey,
  TradeFilterStateKey,
  TradeGridStateKey,
  UseDefaultsStateKey,
  UserInfo,
  UserPreferencesState,
  UserPreferencesStateKey,
  UserStorageFieldName,
  UserStorageState
} from '@approot/models';
import { clone } from '@approot/utilities';
import { isEqual } from 'lodash';
import { Debounce, Throttle } from 'lodash-decorators';
import { Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { AuthService } from '../auth/auth.service';
import { EntityMapperService } from '../entity-mapper/entity-mapper.service';

@Injectable({
  providedIn: 'root'
})
export class UserStorageService {
  private authService = inject(AuthService);
  public readonly storage = signal<UserStorageState>({});
  private entityMapperService = inject(EntityMapperService);

  public getItem<T>(field: UserStorageFieldName): T {
    const val = this.storage()[field];
    return val == null ? val : clone<T>(val);
  }

  public removeItem(field: UserStorageFieldName): void {
    this.storage.update(state => {
      const clonedState = clone(state);
      delete clonedState[field];
      return clonedState;
    });
    this.setUserPreferences();
  }

  public setItem(field: UserStorageFieldName, value: any): void {
    if (!isEqual(this.storage()[field], value)) {
      this.storage.update(state => {
        const clonedState = clone(state);
        clonedState[field] = value;
        return clonedState;
      });
      this.setUserPreferences();
    }
  }

  public loadStorage(store: string | UserStorageState): void {
    try {
      const loadedStore = typeof store === 'string' ? JSON.parse(store) : store;
      this.storage.set(loadedStore);
    } catch {
      this.storage.set({
        [UserPreferencesStateKey]: this.emptyUserPreferencesState()
      });
    }
  }

  public loadFromLocalStorage(userInfo: UserInfo): void {
    // this is needed for backwards compatibility should be removed in the future
    const storage = {};
    [
      ReportingCurrencyStateKey,
      RfqSettingsStateKey,
      UserPreferencesStateKey,
      DciGridDefaultsStateKey,
      EventsBlotterGridStateKey,
      RiskBlotterGridStateKey,
      PNLBlotterGridStateKey,
      CashFlowsBlotterGridStateKey,
      AutoQuoteStorageStateKey,
      TradeGridStateKey,
      TradeFilterStateKey,
      UseDefaultsStateKey,
      DefaultsStateKey,
      RiskBlotterTimeZoneStateKey,
      PNLBlotterTimeZoneStateKey,
      StrategyDetailTabsStateKey,
      ExcludedTradeStatusesKey
    ].forEach(field => {
      let item = localStorage.getItem(field);
      if (field === UserPreferencesStateKey && item == null) {
        item = JSON.stringify(this.emptyUserPreferencesState());
      }

      if (field === StrategyDetailTabsStateKey) {
        item = localStorage.getItem(
          `${StrategyDetailTabsStateKey}-${userInfo.username}`
        );
        localStorage.removeItem(
          `${StrategyDetailTabsStateKey}-${userInfo.username}`
        );
      }

      if (item) {
        try {
          storage[field] = JSON.parse(item);
        } catch {
          storage[field] = item;
        }
        localStorage.removeItem(field);
      }
    });
    this.loadStorage(JSON.stringify(storage));
    this.setUserPreferences();
  }

  public storeChangesObservable<T>(field: UserStorageFieldName): Observable<T> {
    return <Observable<T>>toObservable(this.storage).pipe(
      map(value => value?.[field]),
      distinctUntilChanged()
    );
  }

  public getUserPreferences(): Observable<string> {
    const task = 'getUserPreferences';
    return this.authService.fxPost<string>(
      { task },
      'get-user-info',
      `task=${task}`
    );
  }

  @Throttle(2000, { leading: true, trailing: false })
  public ping() {
    this.doPing();
  }

  public doPing() {
    const prefs = this.getItem<UserPreferencesState>(UserPreferencesStateKey);
    if (!prefs.OtherSettings?.sounds) {
      return;
    }
    const level = prefs.OtherSettings.soundLevel ?? 'quiet';

    try {
      new Audio(`/assets/ping_${level}.mp3`).play();
    } catch (e) {
      console.log('Failed to play ping sound', e);
      prefs.OtherSettings.sounds = false;
      this.setItem(UserPreferencesStateKey, prefs);
    }
  }

  @Debounce(1000)
  private setUserPreferences(): void {
    const task = 'setUserPreferences';
    const storage = this.entityMapperService.cleanConfidentialFields(
      clone(this.storage()),
      'setUserPreferences'
    );
    this.authService
      .fxPost<any>({ task, item: storage }, 'get-user-info', `task=${task}`)
      .subscribe();
  }

  private emptyUserPreferencesState(): UserPreferencesState {
    return { PricingDisplayPrefs: {}, OtherSettings: {} };
  }
}
